import React, { useState, useEffect, useCallback, useRef, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { css } from "@emotion/css"
import Icon from "@amzn/meridian/icon"
import Text from "@amzn/meridian/text"
import Row from "@amzn/meridian/row"
import homeIconTokens from "@amzn/meridian-tokens/base/icon/home"
import clipboardIconTokens from "@amzn/meridian-tokens/base/icon/clipboard"
import searchIconTokens from "@amzn/meridian-tokens/base/icon/search"
import archiveIconTokens from "@amzn/meridian-tokens/base/icon/archive"
import exportIconTokens from "@amzn/meridian-tokens/base/icon/export-large"
import metricsIconTokens from "@amzn/meridian-tokens/base/icon/metrics"
import pencilIconTokens from "@amzn/meridian-tokens/base/icon/pencil"

// ─── Types ───────────────────────────────────────────────────────────────────

interface CommandItem {
  id: string
  label: string
  category: "Pages" | "Actions" | "Recent"
  path: string
  icon: object
  keywords?: string[]
}

// ─── Command Registry ────────────────────────────────────────────────────────

const PAGES: CommandItem[] = [
  { id: "dashboard", label: "Dashboard", category: "Pages", path: "/", icon: homeIconTokens, keywords: ["home", "overview"] },
  { id: "po-list", label: "Purchase Orders", category: "Pages", path: "/po", icon: clipboardIconTokens, keywords: ["po", "purchasing"] },
  { id: "po-create", label: "Create PO", category: "Pages", path: "/po/create", icon: clipboardIconTokens, keywords: ["new", "purchase"] },
  { id: "suppliers", label: "Suppliers", category: "Pages", path: "/suppliers", icon: clipboardIconTokens, keywords: ["vendor"] },
  { id: "exceptions", label: "Exceptions", category: "Pages", path: "/exceptions", icon: clipboardIconTokens, keywords: ["hold", "error"] },
  { id: "ingredients", label: "Ingredients", category: "Pages", path: "/items/ingredients", icon: searchIconTokens, keywords: ["item", "raw"] },
  { id: "recipes", label: "Recipes", category: "Pages", path: "/items/recipes", icon: searchIconTokens, keywords: ["bom", "formula"] },
  { id: "inventory", label: "Inventory", category: "Pages", path: "/inventory", icon: archiveIconTokens, keywords: ["stock", "levels"] },
  { id: "move-stock", label: "Move Stock", category: "Pages", path: "/inventory/move", icon: archiveIconTokens, keywords: ["transfer"] },
  { id: "locations", label: "Locations", category: "Pages", path: "/locations", icon: archiveIconTokens, keywords: ["bin", "zone"] },
  { id: "reorder-alerts", label: "Reorder Alerts", category: "Pages", path: "/reorder-alerts", icon: archiveIconTokens, keywords: ["low", "safety"] },
  { id: "reorder-config", label: "Reorder Config", category: "Pages", path: "/reorder-config", icon: archiveIconTokens, keywords: ["threshold"] },
  { id: "fifo-picking", label: "FIFO Picking", category: "Pages", path: "/pick-recommendation", icon: archiveIconTokens, keywords: ["pick", "recommend"] },
  { id: "quality-hold", label: "Quality Hold", category: "Pages", path: "/quality-hold", icon: archiveIconTokens, keywords: ["hold", "quarantine"] },
  { id: "traceability", label: "Traceability", category: "Pages", path: "/trace", icon: archiveIconTokens, keywords: ["lot", "trace", "fsma"] },
  { id: "work-orders", label: "Work Orders", category: "Pages", path: "/work-orders", icon: pencilIconTokens, keywords: ["manufacturing", "production"] },
  { id: "create-wo", label: "Create Work Order", category: "Pages", path: "/work-orders/create", icon: pencilIconTokens, keywords: ["new", "manufacturing"] },
  { id: "production-plan", label: "Production Plan", category: "Pages", path: "/production-plan", icon: pencilIconTokens, keywords: ["schedule", "plan"] },
  { id: "store-orders", label: "Store Orders", category: "Pages", path: "/store-orders", icon: exportIconTokens, keywords: ["fulfillment", "ship"] },
  { id: "shipping", label: "Shipping", category: "Pages", path: "/shipping", icon: exportIconTokens, keywords: ["dispatch", "delivery"] },
  { id: "supplier-metrics", label: "Supplier Metrics", category: "Pages", path: "/supplier-metrics", icon: metricsIconTokens, keywords: ["analytics", "performance"] },
  { id: "cost-summary", label: "Cost Summary", category: "Pages", path: "/accounting", icon: metricsIconTokens, keywords: ["accounting", "finance"] },
]

const ACTIONS: CommandItem[] = [
  { id: "action-create-po", label: "Create Purchase Order", category: "Actions", path: "/po/create", icon: clipboardIconTokens, keywords: ["new", "po"] },
  { id: "action-create-wo", label: "Create Work Order", category: "Actions", path: "/work-orders/create", icon: pencilIconTokens, keywords: ["new", "manufacturing"] },
  { id: "action-move-stock", label: "Move Stock", category: "Actions", path: "/inventory/move", icon: archiveIconTokens, keywords: ["transfer"] },
  { id: "action-adjust-stock", label: "Adjust Stock", category: "Actions", path: "/inventory", icon: archiveIconTokens, keywords: ["correction"] },
]

// ─── De-duplication ──────────────────────────────────────────────────────────
// A page and an action are "the same thing" when they navigate to the same path
// AND mean the same thing (e.g. the "Create PO" page vs the "Create Purchase
// Order" action both go to /po/create). We collapse those to a single entry,
// preferring the Action (so a destination that has an action shows the action,
// not a redundant page). Distinct entries that merely share a path but differ in
// intent (e.g. the "Inventory" page vs the "Adjust Stock" action, both /inventory)
// are NOT collapsed.

/** Normalize a label so "Create PO" and "Create Purchase Order" compare equal. */
function normalizeLabel(label: string): string {
  return label
    .toLowerCase()
    .replace(/\bpos\b/g, "purchase orders")
    .replace(/\bpo\b/g, "purchase order")
    .replace(/\bwos\b/g, "work orders")
    .replace(/\bwo\b/g, "work order")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
}

const dedupeKey = (item: CommandItem) => `${item.path}|${normalizeLabel(item.label)}`

// Build the canonical item list: drop any Page that an Action already covers.
const ACTION_KEYS = new Set(ACTIONS.map(dedupeKey))
const DEDUPED_ITEMS: CommandItem[] = [
  ...PAGES.filter((p) => !ACTION_KEYS.has(dedupeKey(p))),
  ...ACTIONS,
]

// Render categories in this order.
const CATEGORY_ORDER: CommandItem["category"][] = ["Pages", "Actions", "Recent"]

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = {
  backdrop: css`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(2px);
    z-index: 9999;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding-top: 15vh;
    animation: fadeIn 0.1s ease;
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
  `,
  modal: css`
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(0, 0, 0, 0.05);
    width: 100%;
    max-width: 720px;
    max-height: min(72vh, 640px);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    animation: slideDown 0.15s ease;
    @keyframes slideDown {
      from { opacity: 0; transform: translateY(-8px); }
      to { opacity: 1; transform: translateY(0); }
    }
  `,
  inputWrapper: css`
    padding: 16px;
    border-bottom: 1px solid #e8e8e8;
    display: flex;
    align-items: center;
    gap: 12px;
  `,
  input: css`
    flex: 1;
    border: none;
    outline: none;
    font-size: 16px;
    font-family: inherit;
    color: #16191f;
    &::placeholder {
      color: #aab7c4;
    }
  `,
  kbd: css`
    font-size: 11px;
    padding: 2px 6px;
    border-radius: 4px;
    background: #f2f3f3;
    border: 1px solid #d5dbdb;
    color: #687078;
    font-family: monospace;
  `,
  results: css`
    overflow-y: auto;
    flex: 1;
    min-height: 320px;
    padding: 8px;
  `,
  groupLabel: css`
    padding: 4px 12px 4px;
    font-size: 11px;
    font-weight: 600;
    color: #687078;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  `,
  resultItem: (isActive: boolean) => css`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 12px;
    border-radius: 8px;
    cursor: pointer;
    background: ${isActive ? "#f0f4ff" : "transparent"};
    transition: background 0.1s ease;
    &:hover {
      background: ${isActive ? "#f0f4ff" : "#f7f9fa"};
    }
  `,
  categoryBadge: css`
    font-size: 10px;
    padding: 2px 6px;
    border-radius: 4px;
    background: #f2f3f3;
    color: #687078;
    margin-left: auto;
    white-space: nowrap;
  `,
  empty: css`
    padding: 32px 16px;
    text-align: center;
    color: #687078;
  `,
  footer: css`
    padding: 8px 16px;
    border-top: 1px solid #e8e8e8;
    display: flex;
    align-items: center;
    gap: 16px;
    font-size: 11px;
    color: #687078;
  `,
}

// ─── Fuzzy Match ─────────────────────────────────────────────────────────────

function fuzzyMatch(query: string, item: CommandItem): boolean {
  const q = query.toLowerCase()
  const searchable = [item.label, ...(item.keywords || [])].join(" ").toLowerCase()
  // Simple substring + character-sequence match
  if (searchable.includes(q)) return true
  // Character sequence match (fuzzy)
  let qi = 0
  for (let i = 0; i < searchable.length && qi < q.length; i++) {
    if (searchable[i] === q[qi]) qi++
  }
  return qi === q.length
}

// ─── Component ───────────────────────────────────────────────────────────────

interface CommandPaletteProps {
  open: boolean
  onClose: () => void
}

const CommandPalette: React.FC<CommandPaletteProps> = ({ open, onClose }) => {
  const [query, setQuery] = useState("")
  const [activeIndex, setActiveIndex] = useState(0)
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)
  const resultsRef = useRef<HTMLDivElement>(null)

  // Filter results based on query (against the de-duplicated item list)
  const results = useMemo(() => {
    if (!query.trim()) {
      // No query: show everything (pages + actions), grouped — like the AWS
      // console's command bar. Scrolls within the dialog.
      return DEDUPED_ITEMS
    }
    return DEDUPED_ITEMS.filter((item) => fuzzyMatch(query, item))
  }, [query])

  // Group results by their real category, in a stable display order.
  const grouped = useMemo(() => {
    const groups: Record<string, CommandItem[]> = {}
    for (const item of results) {
      if (!groups[item.category]) groups[item.category] = []
      groups[item.category].push(item)
    }
    return CATEGORY_ORDER.filter((c) => groups[c]?.length).map(
      (c) => [c, groups[c]] as [string, CommandItem[]],
    )
  }, [results])

  // Reset state when opening; focus shortly after the modal mounts its input.
  useEffect(() => {
    if (!open) return
    setQuery("")
    setActiveIndex(0)
    const focusTimer = setTimeout(() => inputRef.current?.focus(), 50)
    return () => clearTimeout(focusTimer)
  }, [open])

  // Scroll active item into view
  useEffect(() => {
    const activeEl = resultsRef.current?.querySelector(`[data-index="${activeIndex}"]`)
    activeEl?.scrollIntoView({ block: "nearest" })
  }, [activeIndex])

  const handleSelect = useCallback((item: CommandItem) => {
    navigate(item.path)
    onClose()
  }, [navigate, onClose])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault()
      setActiveIndex((prev) => Math.min(prev + 1, results.length - 1))
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setActiveIndex((prev) => Math.max(prev - 1, 0))
    } else if (e.key === "Enter") {
      e.preventDefault()
      if (results[activeIndex]) {
        handleSelect(results[activeIndex])
      }
    } else if (e.key === "Escape") {
      e.preventDefault()
      onClose()
    }
  }, [results, activeIndex, handleSelect, onClose])

  // Reset active index when results change
  useEffect(() => {
    setActiveIndex(0)
  }, [query])

  if (!open) return null

  let flatIndex = 0

  return (
    <div className={styles.backdrop} onClick={onClose} role="dialog" aria-modal="true" aria-label="Command palette">
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Search Input */}
        <div className={styles.inputWrapper}>
          <Icon tokens={searchIconTokens} />
          <input
            ref={inputRef}
            className={styles.input}
            type="text"
            placeholder="Search pages and actions..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            aria-label="Search commands"
            role="combobox"
            aria-autocomplete="list"
            aria-controls="command-palette-listbox"
            aria-expanded={results.length > 0}
            aria-activedescendant={
              results[activeIndex] ? `command-palette-option-${activeIndex}` : undefined
            }
          />
          <span className={styles.kbd}>esc</span>
        </div>

        {/* Results */}
        <div
          className={styles.results}
          ref={resultsRef}
          role="listbox"
          id="command-palette-listbox"
          aria-label="Commands"
        >
          {results.length === 0 ? (
            <div className={styles.empty}>
              <Text color="secondary">No results for "{query}"</Text>
            </div>
          ) : (
            grouped.map(([category, items]) => (
              <div key={category}>
                <div className={styles.groupLabel}>{category}</div>
                {items.map((item) => {
                  const idx = flatIndex++
                  return (
                    <div
                      key={item.id}
                      id={`command-palette-option-${idx}`}
                      data-index={idx}
                      role="option"
                      aria-selected={idx === activeIndex}
                      className={styles.resultItem(idx === activeIndex)}
                      onClick={() => handleSelect(item)}
                      onMouseEnter={() => setActiveIndex(idx)}
                    >
                      <Icon tokens={item.icon} />
                      <Text type="b200">{item.label}</Text>
                      <span className={styles.categoryBadge}>{item.category}</span>
                    </div>
                  )
                })}
              </div>
            ))
          )}
        </div>

        {/* Footer hints */}
        <div className={styles.footer}>
          <Row spacing="200" alignmentVertical="center">
            <span className={styles.kbd}>↑↓</span>
            <span>Navigate</span>
          </Row>
          <Row spacing="200" alignmentVertical="center">
            <span className={styles.kbd}>↵</span>
            <span>Open</span>
          </Row>
          <Row spacing="200" alignmentVertical="center">
            <span className={styles.kbd}>esc</span>
            <span>Close</span>
          </Row>
        </div>
      </div>
    </div>
  )
}

export default CommandPalette
